import React from 'react';
import { Button, Card, Input, Space, Typography, Tooltip } from 'antd';
import { DeleteOutlined, PlusOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { PracticeTask } from '../../api/types';

const { Text } = Typography;

export function newTask(): PracticeTask {
  return { id: crypto.randomUUID(), title: '', text: '', tip: null };
}

interface Props {
  tasks: PracticeTask[];
  onChange: (tasks: PracticeTask[]) => void;
}

const TaskListEditor: React.FC<Props> = ({ tasks, onChange }) => {
  const patch = (index: number, fields: Partial<PracticeTask>) =>
    onChange(tasks.map((t, i) => (i === index ? { ...t, ...fields } : t)));

  const move = (index: number, delta: number) => {
    const to = index + delta;
    if (to < 0 || to >= tasks.length) return;
    const next = tasks.slice();
    [next[index], next[to]] = [next[to], next[index]];
    onChange(next);
  };

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      {tasks.length === 0 && (
        <Text type="secondary">
          No tasks yet. Add one below, or bring existing ones over from the Migrate tab.
        </Text>
      )}

      {tasks.map((task, index) => (
        <Card
          key={task.id}
          size="small"
          title={<Text type="secondary">{String(index + 1).padStart(2, '0')}</Text>}
          extra={
            <Space size={4}>
              <Tooltip title="Move up">
                <Button size="small" icon={<ArrowUpOutlined />} disabled={index === 0}
                  onClick={() => move(index, -1)} />
              </Tooltip>
              <Tooltip title="Move down">
                <Button size="small" icon={<ArrowDownOutlined />} disabled={index === tasks.length - 1}
                  onClick={() => move(index, 1)} />
              </Tooltip>
              <Tooltip title="Delete task">
                <Button size="small" danger icon={<DeleteOutlined />}
                  onClick={() => onChange(tasks.filter((_, i) => i !== index))} />
              </Tooltip>
            </Space>
          }
        >
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {/* `?? ''` keeps these inputs controlled and the status check total: a stored
                task may have been written without the field at all. */}
            <Input
              placeholder="Title"
              value={task.title ?? ''}
              status={task.title?.trim() ? undefined : 'error'}
              onChange={(e) => patch(index, { title: e.target.value })}
            />
            <Input.TextArea
              rows={3}
              placeholder="Task text — what the user should actually do"
              value={task.text ?? ''}
              status={task.text?.trim() ? undefined : 'error'}
              onChange={(e) => patch(index, { text: e.target.value })}
            />
            <Input.TextArea
              rows={2}
              placeholder="Tip (optional)"
              value={task.tip ?? ''}
              onChange={(e) => patch(index, { tip: e.target.value || null })}
            />
          </Space>
        </Card>
      ))}

      <Button icon={<PlusOutlined />} onClick={() => onChange([...tasks, newTask()])}>
        Add task
      </Button>
    </Space>
  );
};

export default TaskListEditor;
